import type { AudioOption } from '@/app/types';
import { SITE } from '../../site';
import { TEXT_2_AUDIO_IMAGE } from '../audio-images';
import { getAudioImageIndex } from './get-audio-image-index';
import { TTS_CHINA_VOICE_SOURCE } from './voice-source-china';
import { TTS_GLOBAL_VOICE_SOURCE } from './voice-source-global';

const TTS_LANGUAGE_ORDER_BY_SITE = {
  china: [
    'zh',
    'zhYue',
    'en',
    'ja',
    'ko',
    'es',
    'pt',
    'fr',
    'de',
    'it',
    'ru',
    'ar',
    'hi',
    'id',
    'th',
    'vi',
    'pl',
    'ro',
    'tr',
    'uk',
    'nl',
    'cs',
    'el',
    'fi',
  ],
  global: [
    'en',
    'zh',
    'zhYue',
    'ja',
    'ko',
    'es',
    'pt',
    'fr',
    'de',
    'it',
    'ru',
    'ar',
    'hi',
    'id',
    'th',
    'vi',
    'pl',
    'ro',
    'tr',
    'uk',
    'nl',
    'cs',
    'el',
    'fi',
  ],
} as const;

type TTSSite = keyof typeof TTS_LANGUAGE_ORDER_BY_SITE;
type TTSVoiceLanguageCode = (typeof TTS_LANGUAGE_ORDER_BY_SITE)[TTSSite][number];

export type TTSVoiceLanguage = 'all' | TTSVoiceLanguageCode;

export interface TTSVoiceOption extends AudioOption {
  id: string;
  name: string;
  url: string;
  lang: TTSVoiceLanguage;
  blurHash: string;
}

interface TTSVoiceSourceRecord {
  id: string;
  lang: TTSVoiceLanguageCode;
  name: string;
  urlPath: string;
  description?: string;
}

interface TTSVoiceRegistry {
  languages: readonly TTSVoiceLanguageCode[];
  list: TTSVoiceOption[];
  byLanguage: Record<TTSVoiceLanguage, TTSVoiceOption[]>;
  byId: Record<string, TTSVoiceOption>;
  defaultVoiceId: string;
}

const TTS_MEDIA_ASSET_BASE_URL: Record<TTSSite, string> = {
  china: 'https://image01.vidu.zone/vidu/media-asset/',
  global: 'https://image01.cf.vidu.studio/vidu/media-asset/',
};

const TTS_VOICE_SOURCE_BY_SITE: Record<TTSSite, readonly TTSVoiceSourceRecord[]> = {
  china: TTS_CHINA_VOICE_SOURCE as readonly TTSVoiceSourceRecord[],
  global: TTS_GLOBAL_VOICE_SOURCE as readonly TTSVoiceSourceRecord[],
};

const getTtsVoiceAsset = (voiceId: string, site: TTSSite) => {
  const assetIndex = getAudioImageIndex(voiceId, TEXT_2_AUDIO_IMAGE.length);
  const asset = TEXT_2_AUDIO_IMAGE[assetIndex];

  return {
    img: asset.cover[site],
    blurHash: asset.blurHash,
  };
};

const buildTtsVoiceOption = (voice: TTSVoiceSourceRecord, site: TTSSite): TTSVoiceOption => {
  const asset = getTtsVoiceAsset(voice.id, site);

  return {
    ...voice,
    url: `${TTS_MEDIA_ASSET_BASE_URL[site]}${voice.urlPath}`,
    img: asset.img,
    blurHash: asset.blurHash,
  };
};

const buildTtsVoiceRegistry = (site: TTSSite): TTSVoiceRegistry => {
  const languages = TTS_LANGUAGE_ORDER_BY_SITE[site];
  const list = TTS_VOICE_SOURCE_BY_SITE[site].map((voice) => buildTtsVoiceOption(voice, site));
  const byLanguage = { all: list } as Record<TTSVoiceLanguage, TTSVoiceOption[]>;

  for (const language of languages) {
    byLanguage[language] = list.filter((voice) => voice.lang === language);
  }

  return {
    languages,
    list,
    byLanguage,
    byId: Object.fromEntries(list.map((voice) => [voice.id, voice])),
    defaultVoiceId: list[0]?.id ?? '',
  };
};

export const TTS_VOICE_REGISTRY = buildTtsVoiceRegistry(SITE);
export const TTS_VOICE_LANGUAGES = TTS_VOICE_REGISTRY.languages;
export const TTS_VOICE_LIST = TTS_VOICE_REGISTRY.list;
export const TTS_VOICE_LIST_BY_LANGUAGE = TTS_VOICE_REGISTRY.byLanguage;
export const TTS_VOICE_MAP = TTS_VOICE_REGISTRY.byId;
export const DEFAULT_TTS_VOICE_ID = TTS_VOICE_REGISTRY.defaultVoiceId;
